import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env';

import { appHTTPContext, skipLoadingSpinnerContext } from '@app/config/http-context.config';

// Query params handling
export type QueryParams = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly httpClient = inject(HttpClient);
  private baseUrl = environment.backend.url;

  public getJson<T>(resource: string, params?: HttpParams, reqTag?: string, skipLoadingSpinner = false): Observable<T> {
    return this.httpClient.get<T>(`${this.baseUrl}/${resource}`, {
      params,
      responseType: 'json',
      context: this.buildRequestContext(reqTag, skipLoadingSpinner),
    });
  }

  public getText(
    resource: string,
    params?: HttpParams,
    reqTag?: string,
    skipLoadingSpinner = false,
  ): Observable<string> {
    return this.httpClient.get(`${this.baseUrl}/${resource}`, {
      params,
      responseType: 'text',
      context: this.buildRequestContext(reqTag, skipLoadingSpinner),
    });
  }

  public post<T>(
    resource: string,
    body: unknown,
    params?: HttpParams,
    reqTag?: string,
    skipLoadingSpinner = false,
  ): Observable<T> {
    return this.httpClient.post<T>(`${this.baseUrl}/${resource}`, body, {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      params,
      responseType: 'json',
      context: this.buildRequestContext(reqTag, skipLoadingSpinner),
    });
  }

  public put<T>(
    resource: string,
    body: unknown,
    params?: HttpParams,
    reqTag?: string,
    skipLoadingSpinner = false,
  ): Observable<T> {
    return this.httpClient.put<T>(`${this.baseUrl}/${resource}`, body, {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      params,
      responseType: 'json',
      context: this.buildRequestContext(reqTag, skipLoadingSpinner),
    });
  }

  public patch<T>(
    resource: string,
    body: unknown,
    params?: HttpParams,
    reqTag?: string,
    skipInterceptorErrors = false,
    skipLoadingSpinner = false,
  ): Observable<T> {
    let headers = new HttpHeaders().set('Content-Type', 'application/json-patch+json');
    if (skipInterceptorErrors) {
      headers = headers.set('X-skip-interceptor-error-handling', 'true');
    }

    return this.httpClient.patch<T>(`${this.baseUrl}/${resource}`, body, {
      headers: headers,
      params,
      responseType: 'json',
      context: this.buildRequestContext(reqTag, skipLoadingSpinner),
    });
  }

  private buildRequestContext(reqTag?: string, skipLoadingSpinner = false): HttpContext | undefined {
    if (!reqTag) {
      return undefined;
    }

    return new HttpContext().set(appHTTPContext, reqTag).set(skipLoadingSpinnerContext, skipLoadingSpinner);
  }
}
